import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { EMPTY, Observable, Subject, interval } from 'rxjs';
import { switchMap, tap, catchError, takeUntil, startWith, takeWhile, finalize } from 'rxjs/operators';
import { Location } from '@angular/common';
import { UserAccountService } from '../../../../core/services/user-account.service';
import { Ec2Service } from '../../services/ec2.service';
import { MatDialog } from '@angular/material/dialog';
import { GroupSelectedService } from '../../services/selected.group.service';
import { UserState } from 'src/app/store/user/user.state';
import { Store, select } from '@ngrx/store';
import { getUser } from 'src/app/store/user/user.selector';

@Component({
  standalone: false,
    selector: 'app-liste-ec2',
    templateUrl: './liste-ec2.component.html',
    styleUrls: ['./liste-ec2.component.scss']
})
export class ListeEc2Component implements OnInit, OnDestroy {
    // Définition des colonnes à afficher dans le tableau
    displayedColumns: string[] = ['state', 'name', 'application', 'region', 'instanceId', 'actions',];
    displayedColumnsName: string[] = ['', 'Nom', 'Application', 'Région', 'Instance Id', '', ];

    // Configuration des options du tableau
    tableOptions: any = {
        "columnDefs": [{
            "targets": 0,
            "render": this.renderStatusIcon
        }]
    };

    // Variables pour la gestion des données et états
    dataSource: any;
    loading: boolean = false;

    // Définition des états possibles des instances EC2
    states: any = {
        starting: { icon: 'hourglass_top', label: 'Démarrage en cours' },
        pending: { icon: 'hourglass_top', label: 'Démarrage en cours' },
        running: { icon: 'check_circle', label: 'En fonctionnement' },
        'shutting-down': { icon: 'hourglass_bottom', label: 'Arrêt en cours' },
        terminated: { icon: 'cancel', label: 'Terminée' },
        stopping: { icon: 'hourglass_bottom', label: 'Arrêt en cours' },
        stopped: { icon: 'stop_circle', label: 'Arrêtée' },
    };

    workspace_name: string | null = null;
    intervalId: any = 0;
    user_groups: string[] = [];
    all: boolean = false;
    selectedGroup: any = null;
    user$: Observable<UserState>;
    user_actions: any[] = [];
    private onDestroy$ = new Subject<void>();
    private stopPolling$ = new Subject<void>();
    private isPollingPaused = false;

    // Ajout de la constante pour l'intervalle de rafraîchissement (en ms)
    private readonly REFRESH_INTERVAL = 30000; // 30 secondes

    // Ajouter une Map pour suivre les instances en cours de mise à jour
    private updatingInstances = new Map<string, string>();

    constructor(
		private groupSelected: GroupSelectedService,
		private readonly dialog_: MatDialog,
		private router: Router,
		private location: Location,
		private breakpointObserver: BreakpointObserver,
		private userAccountService: UserAccountService,
		private ec2Service: Ec2Service,
		private readonly ngZone_: NgZone,
		private store: Store<{ user: UserState }>
	) {
		this.user$ = this.store.pipe(select(getUser));
		this.user$.subscribe(user => {
			// Réinitialiser le tableau des actions à chaque mise à jour de l'utilisateur
			this.user_actions = [];

			// Action Start
			const startAction = {
				name: 'start',
				icon: 'play_circle',
				color: 'primary',
				handler: this.startInstance.bind(this),
				condition: this.isInstanceRunning,
				enabled: this.isPermitted(user, "START")
			};
			this.user_actions.push(startAction);

			// Action Stop
			const stopAction = {
				name: 'stop',
				icon: 'stop_circle',
				color: 'warn',
				handler: this.stopInstance.bind(this),
				condition: this.isInstanceRunning,
				enabled: this.isPermitted(user, "STOP")
			};
			this.user_actions.push(stopAction);
		}, error => console.error(error));
	}

    // Méthode pour mettre à jour les actions utilisateur disponibles
    updateUserActions(
        user: any,
        actionName: string,
        icon: string,
        color: string,
        handler: Function,
        condition: Function
    ) {
        // Création de l'action de base
        let action: any = { name: actionName };

        // Si l'utilisateur a la permission, on ajoute les détails de l'action
        if (this.isPermitted(user, actionName.toUpperCase())) {
            action = {
                name: actionName,
                icon: icon,
                color: color,
                handler: handler,
                condition: condition
            };
        }

        // Mise à jour ou ajout de l'action dans le tableau des actions
        const actionIndex = this.user_actions.findIndex(
            existingAction => existingAction.name === actionName
        );

        if (actionIndex < 0) {
            this.user_actions.push(action);
        } else {
            this.user_actions[actionIndex] = action;
        }
    }

    ngOnInit(): void {
        this.loading = true;
        this.startGeneralPolling();
    }

    private startGeneralPolling() {
        if (this.isPollingPaused) {
            return;
        }

        const fetchSource$ = this.groupSelected.selected$.pipe(
            takeUntil(this.onDestroy$),
            tap(() => this.loading = true),
            switchMap((group) => {
                this.selectedGroup = group;
                this.all = false;

                // Créer un observable qui émet périodiquement
                return interval(this.REFRESH_INTERVAL).pipe(
                    startWith(0), // Émet immédiatement pour le premier chargement
                    takeUntil(this.stopPolling$), // Arrêter le polling quand demandé
                    switchMap(() => {
                        if (this.isPollingPaused) {
                            return EMPTY;
                        }
                        if (group && group.group) {
                            return this.ec2Service.getByGroup(group.group);
                        } else {
                            this.all = true;
                            return this.ec2Service.get();
                        }
                    })
                );
            }),
            catchError(err => {
                console.error('Erreur lors du chargement des instances EC2:', err);
                this.loading = false;
                return EMPTY;
            })
        );

        fetchSource$.subscribe(ec2s => {
            if (!this.isPollingPaused) {
                this.dataSource = ec2s;
                this.loading = false;
            }
        });
    }

    private pauseGeneralPolling() {
        this.isPollingPaused = true;
        this.stopPolling$.next();
    }

    private resumeGeneralPolling() {
        this.isPollingPaused = false;
        this.startGeneralPolling();
    }

    ngOnDestroy() {
        this.stopPolling$.next();
        this.stopPolling$.complete();
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    // Méthode pour rendre l'icône d'état
    renderStatusIcon(data: string, type: any, row: any, meta: any) {
        return data == 'running'
            ? '<mat-icon role="img" style="color: #1d8102;" class="mat-icon notranslate mat-tooltip-trigger material-icons mat-icon-no-color caaks-success" aria-hidden="true" data-mat-icon-type="font"> fiber_manual_record </mat-icon>'
            : '<mat-icon role="img" class="mat-icon notranslate mat-tooltip-trigger material-icons mat-icon-no-color " aria-hidden="true" data-mat-icon-type="font"> fiber_manual_record </mat-icon>';
    }

    // Méthode pour obtenir la description de l'état
    getResourceStateDesc(state: string) {
        return this.states[state];
    }

    getDisplayedResourceState(resource: any): string {
        return this.updatingInstances.get(resource.instanceId) || resource.state;
    }

    getResourceStateLabel(state: string): string {
        return this.states[state]?.label || state;
    }

    // Méthode pour obtenir les actions disponibles
    getRowActions() {
        return this.user_actions;
    }

    // Méthode pour vérifier les permissions
    isPermitted(user: any, action: string): boolean {
        if (!user || !user.roles || !Array.isArray(user.roles)) {
            return false;
        }

        try {
            for (let role of user.roles) {
                if (!role || !role.permissions || !Array.isArray(role.permissions)) {
                    continue;
                }

                const hasPermission = role.permissions.some((permission: any) =>
                    permission.policies === action || permission.name === 'full'
                );

                if (hasPermission) {
                    return true;
                }
            }
        } catch (e) {
            console.error('Error in isPermitted', e);
            return false;
        }

        return false;
    }

    // Méthode pour vérifier si une instance est en cours d'exécution
    isInstanceRunning(instance: any) {
        return instance[0] == 'running';
    }

    // Méthode pour suivre l'état d'une instance jusqu'à ce qu'elle atteigne l'état désiré
    private pollInstanceState(instanceId: string, targetState: 'running' | 'stopped'): Observable<any> {
        // Pause le polling général pendant le polling spécifique
        this.pauseGeneralPolling();

        return interval(5000).pipe(
            startWith(0),
            switchMap(() => {
                // Récupérer l'état actuel de l'instance
                if (this.selectedGroup && this.selectedGroup.group) {
                    return this.ec2Service.getByGroup(this.selectedGroup.group);
                } else {
                    return this.ec2Service.get();
                }
            }),
            tap(instances => {
                // Mettre à jour le dataSource avec les dernières données
                this.dataSource = instances;

                // Trouver l'instance spécifique
                const instance = instances.find((i: any) => i.instanceId === instanceId);
                if (instance) {
                    console.log(`État actuel de l'instance ${instanceId}: ${instance.state}`);
                }
            }),
            takeWhile(instances => {
                const instance = instances.find((i: any) => i.instanceId === instanceId);
                if (!instance) {
                    return false;
                }
                const currentState = instance.state;
                const isUpdating = this.updatingInstances.has(instanceId);

                // Arrêter le polling si l'état final est atteint
                const shouldContinue = currentState !== targetState &&
                                     currentState !== 'error' &&
                                     isUpdating;

                if (!shouldContinue) {
                    this.updatingInstances.delete(instanceId);
                }

                return shouldContinue;
            }, true),
            takeUntil(this.onDestroy$),
            finalize(() => {
                this.updatingInstances.delete(instanceId);
                // Reprendre le polling général une fois terminé
                this.resumeGeneralPolling();
            })
        );
    }

    // Méthode pour démarrer une instance
    startInstance(event: any, instance: any) {
        if (this.updatingInstances.get(instance.instanceId)) {
            console.log('Instance déjà en cours de mise à jour');
            return;
        }

        this.updatingInstances.set(instance.instanceId, 'starting');

        this.ngZone_.run(() => {
            // Appeler le service pour démarrer l'instance
            this.ec2Service.start(instance).pipe(
                // Si le démarrage est confirmé et réussi, commencer à suivre l'état
                switchMap((response) => {
                    if (response && response.status) {
                        return this.pollInstanceState(instance.instanceId, 'running');
                    }
                    this.updatingInstances.delete(instance.instanceId);
                    return EMPTY;
                }),
                catchError(error => {
                    console.error('Erreur lors du démarrage de l\'instance:', error);
                    this.updatingInstances.delete(instance.instanceId);
                    return EMPTY;
                })
            ).subscribe();
        });
    }

    // Méthode pour arrêter une instance
    stopInstance(event: any, instance: any) {
        if (this.updatingInstances.get(instance.instanceId)) {
            console.log('Instance déjà en cours de mise à jour');
            return;
        }

        this.updatingInstances.set(instance.instanceId, 'stopping');

        this.ngZone_.run(() => {
            // Appeler le service pour arrêter l'instance
            this.ec2Service.stop(instance).pipe(
                // Si l'arrêt est confirmé et réussi, commencer à suivre l'état
                switchMap((response) => {
                    if (response && response.status) {
                        return this.pollInstanceState(instance.instanceId, 'stopped');
                    }
                    this.updatingInstances.delete(instance.instanceId);
                    return EMPTY;
                }),
                catchError(error => {
                    console.error('Erreur lors de l\'arrêt de l\'instance:', error);
                    this.updatingInstances.delete(instance.instanceId);
                    return EMPTY;
                })
            ).subscribe();
        });
    }

    // Méthode pour obtenir les actions applicables à une ressource
    getApplicableActions(resource: any) {
        if (this.updatingInstances.has(resource.instanceId)) {
            return [];
        }
        return this.getRowActions().filter((action) => {
            if (action.name === 'start') {
                return resource['state'] !== 'running' && resource['state'] !== 'pending';
            } else if (action.name === 'stop') {
                return resource['state'] === 'running';
            }
            return false;
        });
    }

	isActionApplicable(resource: any, actionName: string): boolean {
		if (actionName === 'start') {
			return resource['state'] !== 'running' && resource['state'] !== 'pending';
		} else if (actionName === 'stop') {
			return resource['state'] === 'running';
		}
		return false;
	}
}